import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Clock, Send, ExternalLink, ChevronRight, ChevronDown, Loader2, AlertTriangle } from "lucide-react";
import PreventiviAnalytics from "@/components/PreventiviAnalytics";

const MANC_LABEL = { da_ordinare: "da ordinare", sconosciuto: "non a magazzino", approntamento: "in approntamento" };

const fmtMin = (m) => {
  if (m == null) return "—";
  const h = Math.floor(m / 60), mm = Math.round(m % 60);
  return h ? `${h}h ${mm}m` : `${mm}m`;
};
const fmtDT = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return String(s).slice(0, 16).replace("T", " "); }
};

const eurN = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("it-IT")}`);
const dmy = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("/") : "");

function MotiviCell({ r }) {
  if (!(r.blockers?.length || r.mancanze?.length)) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="space-y-1">
      {(r.blockers || []).map((b, i) => <div key={`b${i}`} className="text-xs text-red-600 dark:text-red-400">• {b}</div>)}
      {(r.mancanze || []).map((m, i) => (
        <div key={`m${i}`} className="text-xs">
          <span className="font-mono font-semibold">{m.codice}</span>
          <span className="text-muted-foreground"> — {MANC_LABEL[m.stato] || m.stato}</span>
          {m.scoperto > 0 && <span className="text-red-600 dark:text-red-400"> · mancano {m.scoperto} pz</span>}
          {m.stato === "da_ordinare" && m.disponibile_ora != null && <span className="text-muted-foreground"> (disp. ora {m.disponibile_ora})</span>}
          {m.stato === "approntamento" && m.data_piena && <span className="text-muted-foreground"> (pronto {dmy(m.data_piena)})</span>}
        </div>
      ))}
    </div>
  );
}

function BoRow({ r }) {
  const [open, setOpen] = useState(false);
  const [det, setDet] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const nx = !open;
    setOpen(nx);
    if (nx && det === null) {
      setLoading(true);
      try { const res = await api.get(`/crm-quotes/${r.id}`); setDet(res.data); }
      catch { setDet(false); }
      finally { setLoading(false); }
    }
  };
  return (
    <>
      <tr className="border-b border-border/50 align-top" data-testid={`bo-richiesta-${r.id}`}>
        <td className="py-2 pr-1">
          <button onClick={toggle} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`bo-expand-${r.id}`} title="Apri dettaglio preventivo">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="py-2 pr-3 font-mono text-xs font-bold">{r.numero || "Bozza"}</td>
        <td className="py-2 pr-3">
          {r.partner_id
            ? <Link to={`/partners/${r.partner_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`bo-cliente-link-${r.id}`}>{r.cliente || "—"}<ExternalLink className="h-3 w-3" /></Link>
            : <span>{r.cliente || "—"}</span>}
        </td>
        <td className="py-2 pr-3 text-muted-foreground">{fmtDT(r.created_at)}</td>
        <td className="py-2 pr-3 text-muted-foreground">{fmtDT(r.inviato_bo_at)}</td>
        <td className="py-2 pr-3"><MotiviCell r={r} /></td>
        <td className="py-2 pr-3 text-right font-semibold">{r.minuti_gestione != null ? fmtMin(r.minuti_gestione) : "—"}</td>
        <td className="py-2 text-right">{eurN(r.totale)}</td>
      </tr>
      {open && (
        <tr className="border-b border-border/50 bg-muted/30" data-testid={`bo-detail-${r.id}`}>
          <td colSpan={8} className="p-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico dettaglio…</div>
            ) : !det ? (
              <div className="text-sm text-muted-foreground">Dettaglio non disponibile.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <span>kWp: <b>{det.kwp_totale ?? "—"}</b></span>
                  <span>Totale: <b>{eurN(det.totale)}</b></span>
                  {det.margine_pct != null && <span>Margine: <b>{Number(det.margine_pct).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%</b></span>}
                  {det.data_richiesta && <span>Consegna richiesta: <b>{dmy(det.data_richiesta)}</b></span>}
                </div>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/60"><tr className="text-left">
                      <th className="px-2 py-1.5 font-semibold">Codice</th>
                      <th className="px-2 py-1.5 font-semibold">Descrizione</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Q.tà</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Prezzo</th>
                      <th className="px-2 py-1.5 text-right font-semibold">Totale</th>
                    </tr></thead>
                    <tbody>
                      {(det.righe || []).map((x, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">{x.codice || "—"}</td>
                          <td className="px-2 py-1">{x.descrizione}</td>
                          <td className="px-2 py-1 text-right font-mono">{x.quantita}</td>
                          <td className="px-2 py-1 text-right font-mono">{eurN(x.prezzo_unitario)}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold">{eurN(x.prezzo_totale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(r.blockers?.length || r.mancanze?.length) ? (
                  <div className="rounded-md border border-red-400/30 bg-red-400/5 p-2">
                    <div className="mb-1 text-xs font-semibold uppercase text-red-600 dark:text-red-400">Motivi mancata apertura automatica</div>
                    <MotiviCell r={r} />
                  </div>
                ) : null}
                {r.partner_id && <Link to={`/partners/${r.partner_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">Apri anagrafica cliente <ExternalLink className="h-3 w-3" /></Link>}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const STATO_BADGE = {
  Bozza: "bg-muted text-muted-foreground",
  "Da Approvare": "bg-amber-400/15 text-amber-600 dark:text-amber-400",
  Aperto: "bg-emerald-400/15 text-emerald-600 dark:text-emerald-400",
};

export default function BackOffice() {
  const [kpi, setKpi] = useState(null);
  const [disp, setDisp] = useState(null);
  const [openRichieste, setOpenRichieste] = useState(false);
  const [openRevisione, setOpenRevisione] = useState(false);
  useEffect(() => {
    api.get("/crm-quotes/backoffice-kpi").then((r) => setKpi(r.data)).catch(() => setKpi(null));
    api.get("/crm-quotes/backoffice-disponibilita").then((r) => setDisp(r.data)).catch(() => setDisp(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="backoffice-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Dashboard BackOffice</h1>
        <p className="text-sm text-muted-foreground">Indicatori di gestione delle richieste e andamento preventivi.</p>
      </div>

      <Card className="p-5" data-testid="bo-kpi-gestione">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          <Clock className="h-4 w-4 text-primary" /> Tempo di gestione delle richieste
        </div>
        <div className="mt-1 font-heading text-4xl font-black tracking-tight" data-testid="bo-kpi-value">
          {kpi ? fmtMin(kpi.avg_minutes) : "—"}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Tempo medio dal momento in cui un preventivo in bozza richiede l'intervento del BackOffice a quando si rende disponibile (aperto).
          {kpi?.avg_minutes_30d != null && <> · Ultimi 30 giorni: <b className="text-foreground">{fmtMin(kpi.avg_minutes_30d)}</b> ({kpi.count_30d})</>}
          {kpi?.count != null && kpi.count > 0 && <> · su {kpi.count} preventivi</>}
        </p>
      </Card>

      <Card className="p-5" data-testid="bo-richieste-table">
        <button type="button" onClick={() => setOpenRichieste((o) => !o)} className="flex w-full items-center gap-2 font-heading text-lg font-bold tracking-tight" data-testid="bo-richieste-toggle">
          {openRichieste ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <Send className="h-4 w-4 text-primary" /> Preventivi inviati al Back Office
          <span className="ml-auto rounded-full bg-primary px-2.5 py-0.5 text-sm font-bold text-primary-foreground" data-testid="bo-richieste-count">{kpi?.richieste?.length || 0}</span>
        </button>
        {openRichieste && (!kpi?.richieste?.length ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="bo-richieste-empty">Nessun preventivo inviato al Back Office per la valutazione.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-8"></th>
                  <th className="py-2 pr-3 font-medium">Numero</th>
                  <th className="py-2 pr-3 font-medium">Cliente</th>
                  <th className="py-2 pr-3 font-medium">Creato il</th>
                  <th className="py-2 pr-3 font-medium">Inviato il</th>
                  <th className="py-2 pr-3 font-medium">Motivi mancata apertura automatica</th>
                  <th className="py-2 pr-3 text-right font-medium">Tempo gestione</th>
                  <th className="py-2 text-right font-medium">Totale</th>
                </tr>
              </thead>
              <tbody>
                {kpi.richieste.map((r) => <BoRow key={r.id} r={r} />)}
              </tbody>
            </table>
          </div>
        ))}
      </Card>

      <Card className="p-5" data-testid="bo-preventivi-revisione">
        <button type="button" onClick={() => setOpenRevisione((o) => !o)} className="flex w-full items-center gap-2 font-heading text-lg font-bold tracking-tight" data-testid="bo-revisione-toggle">
          {openRevisione ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          <AlertTriangle className="h-4 w-4 text-amber-500" /> <span className="text-left">Preventivi da revisionare per disponibilità (ultimi 30 giorni)</span>
          <span className="ml-auto rounded-full bg-amber-500 px-2.5 py-0.5 text-sm font-bold text-white" data-testid="bo-revisione-count">{disp?.preventivi?.length || 0}</span>
        </button>
        {openRevisione && (
          <>
            <p className="mb-3 mt-2 text-xs text-muted-foreground">Tutti i preventivi degli ultimi 30 giorni (Preventivatore/Configuratore + gestionale), stati Da Approvare e Aperto (esclusi i Bozza), con almeno un articolo non pienamente disponibile: da verificare/rivedere in termini di disponibilità prima della conferma.</p>
            {disp === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo in corso…</div>
            ) : !disp?.preventivi?.length ? (
              <p className="text-sm text-muted-foreground" data-testid="bo-revisione-empty">Nessun preventivo da revisionare: tutti gli articoli risultano disponibili.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="py-2 pr-3 font-medium">Numero</th>
                      <th className="py-2 pr-3 font-medium">Cliente</th>
                      <th className="py-2 pr-3 font-medium">Data</th>
                      <th className="py-2 pr-3 font-medium">Stato</th>
                      <th className="py-2 pr-3 font-medium">Articoli critici</th>
                      <th className="py-2 text-right font-medium">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disp.preventivi.map((p) => (
                      <tr key={p.id} className="border-b border-border/50 align-top" data-testid={`bo-revisione-${p.id}`}>
                        <td className="py-2 pr-3 font-mono text-xs font-bold">{p.numero || "Bozza"}</td>
                        <td className="py-2 pr-3">
                          {p.partner_id
                            ? <Link to={`/partners/${p.partner_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">{p.cliente || "—"}<ExternalLink className="h-3 w-3" /></Link>
                            : <span>{p.cliente || "—"}</span>}
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{dmy(p.date)}</td>
                        <td className="py-2 pr-3"><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${STATO_BADGE[p.stato] || "bg-muted"}`}>{p.stato}</span></td>
                        <td className="py-2 pr-3">
                          <div className="space-y-0.5">
                            {p.critici.map((c, i) => (
                              <div key={i} className="text-xs">
                                <span className="font-mono font-semibold">{c.codice}</span>
                                <span className="text-red-600 dark:text-red-400"> · mancano {c.mancante} pz</span>
                                {c.stato === "sconosciuto" && <span className="text-muted-foreground"> (non a magazzino)</span>}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 text-right font-semibold">{eurN(p.totale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <PreventiviAnalytics />
    </div>
  );
}
